import React, { useEffect, useRef, useState } from 'react';
import {
  X,
  Send,
  Save,
  Sparkles,
  RefreshCw,
  Minus,
  Maximize2,
  Minimize2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import { sendEmail, saveDraft, updateDraft, composeWithAI } from '../utils/api';
import { notify, confirmToast } from '../utils/notify';

const TONE_OPTIONS = [
  { id: 'professional', label: 'Professional' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'brief', label: 'Brief' },
  { id: 'formal', label: 'Formal' },
];

export default function ComposeModal({ open, onClose, initial = {}, onSent }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [draftMessageId, setDraftMessageId] = useState(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [tone, setTone] = useState('professional');
  const [aiOpen, setAiOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [winState, setWinState] = useState('normal'); // 'normal' | 'minimized' | 'maximized'
  const statusTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTo(initial.to || '');
    setCc(initial.cc || '');
    setShowCc(Boolean(initial.cc));
    setSubject(initial.subject || '');
    setBody(initial.body || '');
    setDraftMessageId(initial.draftMessageId || null);
    setAiInstruction('');
    setError(null);
    setStatus(null);
    setAiOpen(false);
    setWinState('normal');
  }, [open, initial.to, initial.cc, initial.subject, initial.body, initial.draftMessageId]);

  useEffect(() => () => {
    if (statusTimer.current) clearTimeout(statusTimer.current);
  }, []);

  if (!open) return null;

  const flashStatus = (msg, ms = 1600) => {
    setStatus(msg);
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(null), ms);
  };

  const ccList = cc.split(',').map((s) => s.trim()).filter(Boolean);

  const handleSend = async () => {
    if (!to.trim() || busy) return;
    setBusy('send');
    setError(null);
    try {
      await sendEmail({ to: to.trim(), subject, body, cc: ccList });
      notify.success('Message sent', { description: `To ${to.trim()}` });
      onSent?.();
      onClose();
    } catch (e) {
      const msg = e.message || 'Send failed';
      setError(msg);
      notify.error('Could not send', { description: msg });
    } finally {
      setBusy(null);
    }
  };

  const handleSaveDraft = async () => {
    if (busy) return;
    setBusy('draft');
    setError(null);
    try {
      const out = draftMessageId
        ? await updateDraft({ messageId: draftMessageId, to: to.trim(), subject, body, cc: ccList })
        : await saveDraft({ to: to.trim() || ' ', subject, body, cc: ccList });
      const newId = out?.message_id || out?.draft?.message?.id || draftMessageId;
      if (newId) setDraftMessageId(newId);
      notify.success(draftMessageId ? 'Draft updated' : 'Draft saved');
      onSent?.();
    } catch (e) {
      const msg = e.message || 'Could not save draft';
      setError(msg);
      notify.error('Save failed', { description: msg });
    } finally {
      setBusy(null);
    }
  };

  const handleWriteAI = async () => {
    setBusy('ai-new');
    setError(null);
    try {
      const out = await composeWithAI({
        mode: 'new',
        instruction: aiInstruction || 'Write a clear, concise email.',
        tone,
        recipient_hint: to.trim(),
        subject: '',
        body: '',
      });
      if (out.subject) setSubject(out.subject);
      if (out.body) setBody(out.body);
      flashStatus('AI drafted your message');
    } catch (e) {
      setError(e.message || 'AI failed');
    } finally {
      setBusy(null);
    }
  };

  const handleImproveAI = async () => {
    setBusy('ai-improve');
    setError(null);
    try {
      const out = await composeWithAI({
        mode: 'improve',
        instruction: aiInstruction || '',
        tone,
        subject,
        body,
      });
      if (out.subject) setSubject(out.subject);
      if (out.body) setBody(out.body);
      flashStatus('AI polished your draft');
    } catch (e) {
      setError(e.message || 'AI failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDiscard = async () => {
    if (subject || body || to) {
      const ok = await confirmToast({
        title: 'Discard this draft?',
        description: 'Anything not saved as a draft will be lost.',
        confirmLabel: 'Discard',
        danger: true,
      });
      if (!ok) return;
    }
    onClose();
  };

  const toggleMinimize = () => {
    setWinState((s) => (s === 'minimized' ? 'normal' : 'minimized'));
  };
  const toggleMaximize = () => {
    setWinState((s) => (s === 'maximized' ? 'normal' : 'maximized'));
  };

  const headerSubtitle = subject?.trim() || to?.trim() || 'New message';

  return (
    <div className="compose-anchor" aria-label="Compose email">
      <div
        className={`compose-window ${winState === 'minimized' ? 'minimized' : ''} ${
          winState === 'maximized' ? 'maximized' : ''
        }`}
        role="dialog"
        aria-modal="false"
        aria-label="Compose new email"
      >
        <div
          className="compose-header"
          onClick={(e) => {
            if (e.target.closest('.compose-icon-btn')) return;
            toggleMinimize();
          }}
        >
          <div className="compose-title">
            <Send size={14} />
            <span>{draftMessageId ? 'Editing draft' : 'New message'}</span>
            <span className="compose-title-sub">— {headerSubtitle.slice(0, 60)}{headerSubtitle.length > 60 ? '…' : ''}</span>
          </div>
          <div className="compose-head-actions">
            <button
              type="button"
              className="compose-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleMinimize();
              }}
              aria-label={winState === 'minimized' ? 'Restore' : 'Minimize'}
              title={winState === 'minimized' ? 'Restore' : 'Minimize'}
            >
              {winState === 'minimized' ? <ChevronUp size={16} /> : <Minus size={16} />}
            </button>
            <button
              type="button"
              className="compose-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleMaximize();
              }}
              aria-label={winState === 'maximized' ? 'Restore size' : 'Maximize'}
              title={winState === 'maximized' ? 'Restore size' : 'Full screen'}
            >
              {winState === 'maximized' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button
              type="button"
              className="compose-icon-btn danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDiscard();
              }}
              aria-label="Close"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="compose-body">
          {error && <div className="compose-error">{error}</div>}

          <div className="compose-field">
            <span className="compose-field-label">To</span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              autoComplete="off"
              autoFocus
            />
            {!showCc && (
              <button type="button" className="compose-extra-link" onClick={() => setShowCc(true)}>
                Add Cc
              </button>
            )}
          </div>

          {showCc && (
            <div className="compose-field">
              <span className="compose-field-label">Cc</span>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com, more@example.com"
                autoComplete="off"
              />
              <button type="button" className="compose-extra-link" onClick={() => { setShowCc(false); setCc(''); }}>
                ×
              </button>
            </div>
          )}

          <div className="compose-field">
            <span className="compose-field-label">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
            />
          </div>

          <div className="compose-textarea-wrap">
            <textarea
              className="compose-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message…"
            />
          </div>

          <div className={`compose-ai-panel ${aiOpen ? '' : 'collapsed'}`}>
            <div className="compose-ai-row">
              <strong>
                <Sparkles size={14} /> AI assistant
              </strong>
              <select
                className="compose-pill-btn"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                style={{ minWidth: 130 }}
                aria-label="Tone"
              >
                {TONE_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="compose-pill-btn"
                onClick={handleWriteAI}
                disabled={!!busy}
              >
                {busy === 'ai-new' ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />}
                {busy === 'ai-new' ? 'Writing…' : 'Write'}
              </button>
              <button
                type="button"
                className="compose-pill-btn"
                onClick={handleImproveAI}
                disabled={!!busy || (!body && !subject)}
              >
                {busy === 'ai-improve' ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                {busy === 'ai-improve' ? 'Improving…' : 'Improve'}
              </button>
            </div>
            <textarea
              className="compose-ai-input"
              placeholder="Tell the AI your goal, key points, or tone tweaks…"
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
            />
          </div>

          <div className="compose-footer">
            <button
              type="button"
              className="compose-send-btn"
              onClick={handleSend}
              disabled={!!busy || !to.trim()}
            >
              {busy === 'send' ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
              {busy === 'send' ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              className={`compose-pill-btn ${aiOpen ? 'active' : ''}`}
              onClick={() => setAiOpen((v) => !v)}
              title="Toggle AI assistant"
            >
              <Sparkles size={12} />
              AI {aiOpen ? '▾' : '▸'}
            </button>
            <button
              type="button"
              className="compose-pill-btn"
              onClick={handleSaveDraft}
              disabled={!!busy}
              title={draftMessageId ? 'Update Gmail draft' : 'Save as draft'}
            >
              {busy === 'draft' ? <Loader2 size={12} className="spin" /> : <Save size={12} />}
              {busy === 'draft' ? 'Saving…' : draftMessageId ? 'Update draft' : 'Save draft'}
            </button>
            <span className={`compose-status ${status ? 'ok' : ''}`}>{status}</span>
            <span className="sp" />
            <button
              type="button"
              className="compose-icon-btn danger"
              onClick={handleDiscard}
              title="Discard"
              aria-label="Discard draft"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
